/**
 * The whole library as one list.
 *
 * `index.ts` exposes the drum and bass *recipes*; this file is the level above it — every
 * sound the library can make, drums and 808s and instruments and FX and loops alike, in one
 * order, each behind the same render closure. The installer, the pack browser and the
 * first-run generator all read this and nothing else, so adding a sound to a catalogue file
 * is the only step needed to ship it.
 */

import { basses, eightOhEights } from './bass';
import { claps, hats, kicks, percussion, snares } from './drums';
import { soundEntry, type LibraryEntry, type LibraryFamily } from './entry';
import { chordEntriesList, melodicEntries } from './melodic';
import { fxEntries } from './fx';
import { loopEntries } from './loops';
import type { LibrarySound } from './model';
import { noteSlug } from './notes';

/**
 * The MIDI note a pitched drum-or-bass recipe sounds at, recovered from its slug.
 *
 * The recipe model has no root field — a kick does not have one — so the note lives in the
 * identifier, which is also what the file name and the pad label are built from. Recovering it
 * here is what puts `root:` on the 808s and the basses, and that tag is the difference between
 * a grid of noises and two octaves you can play a bass line on.
 */
function rootMidiFor(sound: LibrarySound): number | undefined {
  const suffix = sound.id.slice(sound.id.lastIndexOf('-') + 1);
  if (sound.category === '808') {
    for (let midi = 24; midi <= 48; midi++) if (noteSlug(midi) === suffix) return midi;
    return undefined;
  }
  if (sound.category === 'bass') {
    for (const midi of [28, 33, 36]) if (noteSlug(midi) === suffix) return midi;
    return undefined;
  }
  return undefined;
}

function entriesFor(sounds: readonly LibrarySound[]): LibraryEntry[] {
  return sounds.map((sound) => {
    const rootMidi = rootMidiFor(sound);
    return soundEntry(sound, rootMidi === undefined ? {} : { rootMidi });
  });
}

/** The drum one-shots, in kit order. */
export const drumEntries: readonly LibraryEntry[] = entriesFor([
  ...kicks,
  ...snares,
  ...hats,
  ...claps,
  ...percussion,
]);

/** The chromatic 808 run, then the playable bass timbres. */
export const bassEntries: readonly LibraryEntry[] = entriesFor([...eightOhEights, ...basses]);

/**
 * Every sound the library can make, in a stable order. The order is the order a first run
 * renders them in when no pack has asked for something sooner, so the drums — the sounds a
 * board is unplayable without — come first.
 */
export const LIBRARY_CATALOGUE: readonly LibraryEntry[] = [
  ...drumEntries,
  ...bassEntries,
  ...loopEntries,
  ...fxEntries,
  ...chordEntriesList,
  ...melodicEntries,
];

const byID = new Map<string, LibraryEntry>(LIBRARY_CATALOGUE.map((entry) => [entry.id, entry]));

export function libraryEntry(id: string): LibraryEntry | undefined {
  return byID.get(id);
}

export function libraryEntriesInFamily(family: LibraryFamily): LibraryEntry[] {
  return LIBRARY_CATALOGUE.filter((entry) => entry.family === family);
}

/** Resolves a list of identifiers to entries, skipping any the catalogue does not have. */
export function libraryEntriesWithIDs(ids: readonly string[]): LibraryEntry[] {
  const entries: LibraryEntry[] = [];
  for (const id of ids) {
    const entry = byID.get(id);
    if (entry !== undefined) entries.push(entry);
  }
  return entries;
}
